import React from 'react';
import { Button, Card, Table } from 'react-bootstrap';

import { ProjectCostCollaborator } from '../../../../types';
import { useTypedProjectCostContext } from '../../useTypedProjectCostContext';
import TableControls from '../TableControls';
import { formatOptionalCurrencyFromCents } from '../../utils';

type CollaboratorsTableProps = {
  companyId?: number;
  hideCompanyColumn?: boolean;
};

const CollaboratorsTable: React.FC<CollaboratorsTableProps> = ({ companyId, hideCompanyColumn = false }) => {
  const {
    getPagedCollaborators,
    handleEditCollaborator,
    handleDelete,
    saving,
  } = useTypedProjectCostContext<{
    getPagedCollaborators: (companyId?: number | null) => {
      pageItems: ProjectCostCollaborator[];
      totalItems: number;
      totalPages: number;
      currentPage: number;
    };
    handleEditCollaborator: (item: ProjectCostCollaborator) => void;
    handleDelete: (path: string, message: string) => Promise<void>;
    saving: boolean;
  }>();

  const paged = getPagedCollaborators(companyId ?? null);
  const colSpan = hideCompanyColumn ? 7 : 8;

  return (
    <Card>
      <Card.Header>Colaboradores</Card.Header>
      <Card.Body className="table-responsive">
        <TableControls
          tableKey="collaborators"
          placeholder="Buscar por nome, e-mail, Jira, empresa, função..."
          totalItems={paged.totalItems}
          totalPages={paged.totalPages}
          currentPage={paged.currentPage}
        />
        <Table size="sm" hover>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Mapeamento Jira</th>
              <th>Usuário (ID)</th>
              {!hideCompanyColumn && <th>Empresa</th>}
              <th>Função</th>
              <th>Taxa override</th>
              <th>Tipo</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageItems.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-3">
                  Nenhum registro encontrado.
                </td>
              </tr>
            ) : (
              paged.pageItems.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    {item.jira_account_id || item.jira_display_name
                      ? (
                        <>
                          {item.jira_display_name || '—'}
                          {item.jira_account_id ? ` (${item.jira_account_id})` : ''}
                        </>
                      )
                      : '—'}
                  </td>
                  <td>{item.user_id || '—'}</td>
                  {!hideCompanyColumn && <td>{item.company?.name || '—'}</td>}
                  <td>{item.role?.name || '—'}</td>
                  <td>{formatOptionalCurrencyFromCents(item.hourly_rate_override)}</td>
                  <td>{item.is_third_party ? 'Terceiro' : 'Próprio'}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleEditCollaborator(item)}
                      disabled={saving}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => void handleDelete(
                        `/api/projects/costs/collaborators/${item.id}`,
                        `Excluir colaborador "${item.name}"?`,
                      )}
                      disabled={saving}
                    >
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default CollaboratorsTable;
